import { useIntl } from 'react-intl';

import { useJobSearchAggregationsQuery } from '@/graphql/generated';
import { getSalaryFormatOptions } from '@/helpers/salary-utils';
import { unscrambleStringId } from '@/helpers/xing-one';
import { SearchQueryFiltersLegacy } from '@/types';

export type ExtractedX1Filters = {
  x1Filters: {
    employmentType?: string[];
    careerLevel?: string[];
    discipline?: string[];
    salary?: string;
    industry?: string[];
    benefit?: string[];
    country?: string[];
    city?: string[];
  };
  translatedFilters: string[];
  isLoading?: boolean;
};

const mapLegacyFilterNames = {
  type: { aggregation: 'employmentTypes', property: 'employmentType' },
  level: { aggregation: 'careerLevels', property: 'careerLevel' },
  discipline: { aggregation: 'disciplines', property: 'discipline' },
  industry: { aggregation: 'industries', property: 'industry' },
  benefits: { aggregation: 'benefits', property: 'benefit' },
  country: { aggregation: 'countries', property: 'country' },
  city: { aggregation: 'cities', property: 'city' },
  remote_option: { aggregation: 'remoteOptions', property: 'remoteOption' },
} as const;

const formatSalary = (formatNumber, [min, max]: string[]) => {
  const formatNumberOptions = getSalaryFormatOptions('EUR');
  return `${formatNumber(Number(min), formatNumberOptions)} – ${formatNumber(Number(max), formatNumberOptions)}`;
};

export const useExtractX1Filters = (legacyFilters: SearchQueryFiltersLegacy): ExtractedX1Filters => {
  const intl = useIntl();
  const legacyFiltersEntries = Object.entries(legacyFilters) as [keyof SearchQueryFiltersLegacy, string[]][];

  const { loading, data } = useJobSearchAggregationsQuery({
    errorPolicy: 'all',
    skip: legacyFiltersEntries.length === 0,
  });
  const x1Aggregations = data?.jobSearchByQuery?.aggregations;
  if (loading || !x1Aggregations) {
    return { x1Filters: {}, translatedFilters: [], isLoading: loading };
  }

  // We get the scrambled x1 filters from the existing legacy filters
  const x1Filters = legacyFiltersEntries
    .map(([legacyFilterName, legacyFilterValues]) => {
      if (!Array.isArray(legacyFilterValues)) {
        return false;
      }

      if (legacyFilterName === 'company') {
        return [legacyFilterName, legacyFilterValues];
      }
      if (legacyFilterName === 'salary') {
        // Remove unscrambling once backend delivers unscrambled salary values
        const unscrambledSalaryValues = legacyFilterValues.map((value) => unscrambleStringId(value));
        return [legacyFilterName, `${unscrambledSalaryValues[0]}_${unscrambledSalaryValues[1]}`];
      }

      if (!mapLegacyFilterNames[legacyFilterName]) {
        return false;
      }
      const { aggregation, property } = mapLegacyFilterNames[legacyFilterName];
      const x1Aggregation = x1Aggregations[aggregation];

      const x1FilterIds = legacyFilterValues.reduce<string[]>((filterIds, legacyFilterId) => {
        const filter = x1Aggregation?.find(
          (x1Filter) => unscrambleStringId(x1Filter?.id) === unscrambleStringId(legacyFilterId)
        );

        if (!filter?.id) {
          return filterIds;
        }

        return [...filterIds, filter.id];
      }, []);

      return [property, x1FilterIds];
    })
    .filter(Boolean);

  // We get the filter translations from the x1 results
  const translatedFilters = legacyFiltersEntries.reduce((list, [legacyFilterName, legacyFilterValues]) => {
    if (!Array.isArray(legacyFilterValues)) {
      return list;
    }

    if (legacyFilterName === 'company') {
      return [intl.formatMessage({ id: 'JOBS_SEARCH_COMPANY_CULTURE_FILTER_VALUE' }), ...list];
    }
    if (legacyFilterName === 'salary') {
      // Remove unscrambling once backend delivers unscrambled salary values
      const unscrambledSalaryValues = legacyFilterValues.map((value) => unscrambleStringId(value));
      return [...list, formatSalary(intl.formatNumber, unscrambledSalaryValues)];
    }

    if (!mapLegacyFilterNames[legacyFilterName]) {
      return list;
    }
    const { aggregation, property } = mapLegacyFilterNames[legacyFilterName];
    const x1Aggregation = x1Aggregations[aggregation];

    const filterTranslations = legacyFilterValues.reduce<string[]>((filterList, legacyFilterId) => {
      const filter = x1Aggregation?.find(
        (x1Filter) => unscrambleStringId(x1Filter?.id) === unscrambleStringId(legacyFilterId)
      );

      if (!filter?.[property]?.localizationValue) {
        return filterList;
      }

      return [...filterList, filter[property].localizationValue];
    }, []);

    return [...list, ...filterTranslations];
  }, []);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore false values already filtered by .filter(Boolean)
  return { x1Filters: Object.fromEntries(x1Filters), translatedFilters };
};
